import React, { useRef, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FaTimes, FaShieldAlt } from 'react-icons/fa';
import recaptchaConfig from '../../config/recaptcha';
import { FocusTrap } from '../../utils';
import '../forms/Contact.css';

const ReCaptchaModal = ({ isOpen, onVerify, isLoading = false }) => {
  const modalRef = useRef(null);
  const focusTrapRef = useRef(null);
  const recaptchaRef = useRef(null);
  const [recaptchaError, setRecaptchaError] = React.useState(null);

  // Handle modal state changes (scroll prevention and focus trap)
  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Store original overflow values
      const originalBodyOverflow = document.body.style.overflow;
      const originalHtmlOverflow = document.documentElement.style.overflow;
      
      // Prevent body and html scroll
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      
      // Initialize and activate focus trap
      focusTrapRef.current = new FocusTrap(modalRef.current);
      focusTrapRef.current.activate();
      
      return () => {
        // Restore original overflow when modal closes
        document.body.style.overflow = originalBodyOverflow;
        document.documentElement.style.overflow = originalHtmlOverflow;
        
        // Deactivate and cleanup focus trap
        if (focusTrapRef.current) {
          focusTrapRef.current.deactivate();
          focusTrapRef.current = null;
        }
      };
    }
  }, [isOpen]);


  const handleRecaptchaChange = (value) => {
    if (value) {
      onVerify(value);
    }
  };

  const handleRecaptchaExpired = () => {
    // Reset reCAPTCHA if it expires
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleRecaptchaError = (error) => {
    setRecaptchaError('Failed to load reCAPTCHA. Please refresh the page and try again.');
  };

  // Disable backdrop click - force CAPTCHA completion
  const handleBackdropClick = (e) => {
    // Do nothing - prevent closing by clicking outside
  };

  if (!isOpen) return null;

  return (
    <div className="recaptcha-modal-overlay pad-h-6" onClick={handleBackdropClick}>
      <div className="recaptcha-modal-content" ref={modalRef}>
        <div className="recaptcha-modal-header">
          <div className="recaptcha-modal-icon">
            <FaShieldAlt />
          </div>
          <h3>Security Verification Required</h3>
          <p>Complete the verification below to send your message securely.</p>
        </div>
        
        <div className="recaptcha-modal-body">
          {recaptchaError ? (
            <div style={{ 
              padding: '20px', 
              backgroundColor: '#fee', 
              border: '1px solid #fcc', 
              borderRadius: '8px',
              color: '#c33',
              textAlign: 'center'
            }}>
              <p>{recaptchaError}</p>
              <button 
                onClick={() => window.location.reload()}
                style={{
                  marginTop: '10px',
                  padding: '8px 16px',
                  backgroundColor: '#c33',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Refresh Page
              </button>
            </div>
          ) : (
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={recaptchaConfig.siteKey}
              onChange={handleRecaptchaChange}
              onExpired={handleRecaptchaExpired}
              onErrored={handleRecaptchaError}
              theme="light"
            />
          )}
          
          {isLoading && (
            <div className="recaptcha-loading">
              <div className="loading-spinner"></div>
              <p>Sending your message...</p>
            </div>
          )}
        </div>
        
        <div className="recaptcha-modal-footer">
          <p className="recaptcha-note">
            Verification is required to send your message. This helps protect against spam and ensures your inquiry reaches us safely.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReCaptchaModal;
